import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Input,
  message,
  Image,
  Empty,
  Skeleton,
  Tag,
  Button,
  Checkbox,
  Modal,
  Space,
  Pagination,
  List,
} from 'antd';
import ModuleNav from './ModuleNav';
import { listGallery, listGalleryUsers } from '../../services/aiImageMaskingApi';

const { Title, Text } = Typography;

const UserGallery = () => {
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [compareVisible, setCompareVisible] = useState(false);
  const [compareOrder, setCompareOrder] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [imageSearch, setImageSearch] = useState('');
  const [userPickerVisible, setUserPickerVisible] = useState(false);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(10);
  const [userTotal, setUserTotal] = useState(0);
  const [userLoading, setUserLoading] = useState(false);
  const [userItems, setUserItems] = useState([]);

  const trimmedUser = selectedUser.trim();
  const trimmedImageSearch = imageSearch.trim().toLowerCase();

  const loadGallery = useCallback(
    async (targetUser) => {
      if (!targetUser) {
        setGalleryItems([]);
        return;
      }
      setLoading(true);
      try {
        const data = await listGallery({ user_id: targetUser });
        setGalleryItems(Array.isArray(data?.items) ? data.items : []);
      } catch (error) {
        message.error(error.message || 'Unable to load gallery');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadGallery(trimmedUser);
  }, [trimmedUser, loadGallery]);

  const loadUsers = useCallback(
    async ({ page = userPage, pageSize = userPageSize, search = userSearchTerm } = {}) => {
      setUserLoading(true);
      try {
        const data = await listGalleryUsers({
          page,
          page_size: pageSize,
          search: search || undefined,
        });
        setUserItems(Array.isArray(data?.items) ? data.items : []);
        setUserTotal(data?.total || 0);
        setUserPage(page);
        setUserPageSize(pageSize);
        setUserSearchTerm(search);
        setUserSearchInput(search);
      } catch (error) {
        message.error(error.message || 'Unable to load users');
      } finally {
        setUserLoading(false);
      }
    },
    [userPage, userPageSize, userSearchTerm]
  );

  const filteredGalleryItems = useMemo(() => {
    if (!trimmedImageSearch) {
      return galleryItems;
    }
    return galleryItems.filter((entry) => {
      const haystacks = [
        entry.swatch_name || '',
        entry.swatch_description || '',
        entry.lifestyle_hash || '',
      ];
      return haystacks.some((value) => value && value.toLowerCase().includes(trimmedImageSearch));
    });
  }, [galleryItems, trimmedImageSearch]);

  const groupedGallery = useMemo(() => {
    if (!filteredGalleryItems.length) {
      return [];
    }
    const groups = filteredGalleryItems.reduce((acc, entry) => {
      const key = entry.lifestyle_hash || String(entry.id);
      if (!acc[key]) {
        acc[key] = {
          hash: key,
          original: entry.original_url,
          overlays: [],
        };
      }
      if (!acc[key].original && entry.original_url) {
        acc[key].original = entry.original_url;
      }
      acc[key].overlays.push(entry);
      return acc;
    }, {});
    return Object.values(groups);
  }, [filteredGalleryItems]);

  const entryMap = useMemo(() => {
    const map = new Map();
    filteredGalleryItems.forEach((entry) => {
      const key = String(entry.id);
      map.set(key, { ...entry, id: key, isOriginal: false });
    });
    groupedGallery.forEach((group) => {
      if (group.original) {
        const key = `original-${group.hash}`;
        map.set(key, {
          id: key,
          overlay_url: group.original,
          swatch_name: 'Original Lifestyle',
          swatch_id: group.hash,
          updated_at: null,
          isOriginal: true,
        });
      }
    });
    return map;
  }, [galleryItems, groupedGallery]);

  useEffect(() => {
    // prune selections that no longer exist in current result set
    setSelectedIds((prev) => prev.filter((id) => entryMap.has(id)));
  }, [entryMap]);

  useEffect(() => {
    setSelectedIds([]);
  }, [trimmedUser]);

  const selectedEntries = useMemo(() => selectedIds.map((id) => entryMap.get(id)).filter(Boolean), [selectedIds, entryMap]);

  const toggleSelection = (entryId) => {
    setSelectedIds((prev) => (prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]));
  };

  const clearSelection = () => setSelectedIds([]);

  const openCompareModal = () => {
    if (!selectedEntries.length) {
      message.info('Select at least one overlay to compare.');
      return;
    }
    setCompareOrder(selectedEntries);
    setCompareVisible(true);
  };

  const handleDragStart = (event, index) => {
    if (event?.dataTransfer) {
      event.dataTransfer.setData('text/plain', String(index));
      event.dataTransfer.effectAllowed = 'move';
    }
    setDragIndex(index);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) {
      return;
    }
    setCompareOrder((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(index, 0, moved);
      return updated;
    });
    setDragIndex(null);
  };

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    setUserPickerVisible(false);
    setImageSearch('');
  };

  useEffect(() => {
    if (!compareVisible) {
      setCompareOrder([]);
      setDragIndex(null);
    }
  }, [compareVisible]);

  const gridColumns = useMemo(() => {
    const count = compareOrder.length || 1;
    if (count === 1) return '1fr';
    if (count === 2) return 'repeat(2, 1fr)';
    if (count === 3) return 'repeat(3, minmax(0, 1fr))';
    return 'repeat(auto-fit, minmax(240px, 1fr))';
  }, [compareOrder]);

  return (
    <div className="ai-image-masking-page">
      <ModuleNav activeKey="gallery" />

      <Card className="shadow-sm mb-4" bodyStyle={{ padding: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={8}>
            <Title level={4} className="mb-0">
              User Gallery
            </Title>
            <Text type="secondary">Filter by user and compare stored tryouts.</Text>
          </Col>
          <Col xs={24} lg={8}>
            <Space direction="vertical" size="small" className="w-100">
              <Input.Search
                placeholder="Search images by swatch, description, or hash"
                allowClear
                value={imageSearch}
                onChange={(e) => setImageSearch(e.target.value)}
              />
            </Space>
          </Col>
          <Col xs={24} lg={8}>
            <div className="d-flex flex-wrap justify-content-lg-end align-items-center gap-2">
              <Space align="center" size="small" className="flex-grow-1 justify-content-lg-end">
                {trimmedUser ? (
                  <>
                    <span className="fw-semibold">User: {trimmedUser}</span>
                    <Button
                      size="small"
                      onClick={() => {
                        setUserPickerVisible(true);
                        loadUsers({ page: 1, search: trimmedUser });
                      }}
                    >
                      Change
                    </Button>
                  </>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      setUserPickerVisible(true);
                      loadUsers({ page: 1, search: '' });
                    }}
                  >
                    Select User
                  </Button>
                )}
              </Space>
              <Space wrap>
                {selectedIds.length > 0 && (
                  <Button size="small" onClick={clearSelection}>
                    Clear selection
                  </Button>
                )}
                <Button
                  type="primary"
                  size="small"
                  onClick={openCompareModal}
                  disabled={!selectedIds.length}
                >
                  Compare selected ({selectedIds.length})
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <Card className="shadow-sm">
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !trimmedUser ? (
        <Card className="shadow-sm">
          <Empty description="Select a user to load saved try-ons" />
        </Card>
      ) : groupedGallery.length === 0 ? (
        <Card className="shadow-sm">
          <Empty description="No gallery entries found for this user" />
        </Card>
      ) : (
        groupedGallery.map((group) => (
          <Card key={group.hash} className="shadow-sm mb-4" bodyStyle={{ padding: 20 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6} lg={5}>
                <div className="d-flex align-items-center gap-2">
                  <Tag color="blue" style={{ marginBottom: 0 }}>
                    {trimmedUser || 'Selected user'}
                  </Tag>
                  <Text strong className="text-truncate">Original lifestyle</Text>
                </div>
                {group.original ? (
                  <div
                    style={{
                      position: 'relative',
                      border: selectedIds.includes(`original-${group.hash}`) ? '2px solid #1677ff' : '1px solid #f0f0f0',
                      borderRadius: 12,
                      marginTop: 12,
                      cursor: 'pointer',
                      minHeight: 320,
                      height: '100%',
                      overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                    }}
                    onClick={() => toggleSelection(`original-${group.hash}`)}
                  >
                    <Image
                      src={group.original}
                      alt="Original lifestyle"
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 320,
                        objectFit: 'cover',
                        filter: selectedIds.includes(`original-${group.hash}`) ? 'brightness(0.92)' : 'none',
                      }}
                      preview={false}
                    />
                    <Checkbox
                      checked={selectedIds.includes(`original-${group.hash}`)}
                      onChange={() => toggleSelection(`original-${group.hash}`)}
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        background: 'rgba(255,255,255,0.95)',
                        borderRadius: 999,
                        padding: '2px 6px',
                        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.2)',
                      }}
                    >
                      Select
                    </Checkbox>
                  </div>
                ) : (
                  <Empty description="Original unavailable" className="mt-3" />
                )}
              </Col>
              <Col xs={24} md={18} lg={19}>
                <Text strong>Overlays</Text>
                <Row gutter={[16, 16]} className="mt-2">
                  {group.overlays.map((entry) => {
                    const entryId = String(entry.id);
                    const isSelected = selectedIds.includes(entryId);
                    return (
                      <Col xs={24} sm={12} lg={8} key={entry.id}>
                        <div
                          style={{
                            border: isSelected ? '2px solid #1677ff' : '1px solid #f0f0f0',
                            borderRadius: 14,
                            overflow: 'hidden',
                            background: '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                            cursor: 'pointer',
                            minHeight: 320,
                          }}
                          onClick={() => toggleSelection(entryId)}
                        >
                          <div style={{ position: 'relative', flex: 1 }}>
                            <Image
                              src={entry.overlay_url}
                              alt={entry.swatch_name || `Swatch ${entry.swatch_id}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                minHeight: 260,
                                objectFit: 'cover',
                              }}
                              preview={false}
                            />
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleSelection(entryId)}
                              onClick={(event) => event.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                background: 'rgba(255,255,255,0.95)',
                                borderRadius: 999,
                                padding: '2px 6px',
                                boxShadow: '0 2px 6px rgba(15,23,42,0.2)',
                              }}
                            >
                              Select
                            </Checkbox>
                          </div>
                          <div style={{ padding: '12px 14px', borderTop: '1px solid #f0f0f0' }}>
                            <Text strong className="d-block">
                              {entry.swatch_name || `Swatch ${entry.swatch_id}`}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                              {entry.updated_at ? new Date(entry.updated_at).toLocaleString() : ''}
                            </Text>
                            <Space size="small" className="mt-2">
                              <Button
                                type="link"
                                size="small"
                                href={entry.overlay_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Open
                              </Button>
                            </Space>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Col>
            </Row>
          </Card>
        ))
      )}
      <Modal
        title="Select User"
        open={userPickerVisible}
        onCancel={() => setUserPickerVisible(false)}
        footer={null}
        width={560}
      >
        <Space direction="vertical" size="middle" className="w-100">
          <Input.Search
            placeholder="Search users by ID"
            allowClear
            enterButton="Search"
            value={userSearchInput}
            onChange={(e) => setUserSearchInput(e.target.value)}
            onSearch={(value) => loadUsers({ page: 1, search: value.trim() })}
          />
          <List
            bordered
            dataSource={userItems}
            loading={userLoading}
            locale={{ emptyText: 'No users found' }}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button key="choose" type="link" onClick={() => handleUserSelect(item.user_id)}>
                    Select
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={item.user_id}
                  description={`${item.total_entries || 0} tryouts · ${
                    item.last_used ? new Date(item.last_used).toLocaleString() : 'No timestamp'
                  }`}
                />
              </List.Item>
            )}
          />
          <Pagination
            size="small"
            current={userPage}
            pageSize={userPageSize}
            total={userTotal}
            showSizeChanger
            onChange={(page, pageSize) => loadUsers({ page, pageSize, search: userSearchTerm })}
          />
        </Space>
      </Modal>

      <Modal
        open={compareVisible}
        onCancel={() => setCompareVisible(false)}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
      >
        <div className="p-4">
          <Row justify="space-between" align="middle" className="mb-3">
            <Col>
              <Title level={4} className="mb-0">
                Compare Selected Tryouts
              </Title>
              <Text type="secondary">Drag images to reorder. Layout adapts automatically.</Text>
            </Col>
            <Col>
              <Button onClick={() => setCompareVisible(false)}>Close</Button>
            </Col>
          </Row>
          {compareOrder.length === 0 ? (
            <Empty description="No images selected" />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: 16,
              }}
            >
              {compareOrder.map((entry, index) => (
                <div
                  key={entry.id}
                  draggable
                  onDragStart={(event) => handleDragStart(event, index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 12,
                    padding: 12,
                    background: '#fff',
                    cursor: 'move',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Text strong>{entry.swatch_name || `Swatch ${entry.swatch_id}`}</Text>
                    <Tag>#{index + 1}</Tag>
                  </div>
                  <Image
                    src={entry.overlay_url}
                    alt={entry.swatch_name || `Swatch ${entry.swatch_id}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: 320,
                      borderRadius: 10,
                      objectFit: 'cover',
                      background: '#000',
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {entry.updated_at ? new Date(entry.updated_at).toLocaleString() : ''}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default UserGallery;


